import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Award } from "lucide-react";
import { Card, CardContent } from "./ui/card";
import { Progress } from "./ui/progress";
import AchievementBadge from "./AchievementBadge";
import { API } from "../App";
import { authFetch } from "../lib/auth";

const formatDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
    : "";

/** variant="strip" → compact Dashboard row. variant="full" → Profile grid. */
const AchievementsCard = ({ variant = "strip" }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await authFetch(`${API}/achievements`);
        if (res.ok && alive) setData(await res.json());
      } catch {
        /* badges are decoration — never block the page */
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  if (!data) return null;

  const earned = data.badges.filter((b) => b.earned);
  const nextUp = data.next_up;

  if (variant === "strip") {
    return (
      <motion.div
        className="mb-12"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        data-testid="achievements-strip"
      >
        <Card className="bg-card border-border">
          <CardContent className="p-6">
            <div className="flex flex-col lg:flex-row lg:items-center gap-6">
              <div className="flex items-center gap-4 lg:w-56">
                <div className="w-14 h-14 flex items-center justify-center bg-primary/10">
                  <Award className="w-7 h-7 text-primary" />
                </div>
                <div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-bold text-primary" data-testid="achievements-earned-count">
                      {data.earned_count}
                    </span>
                    <span className="text-muted-foreground">/ {data.total}</span>
                  </div>
                  <p className="text-sm text-muted-foreground">Badges earned</p>
                </div>
              </div>

              <div className="hidden lg:block w-px h-16 bg-border" />

              {earned.length > 0 ? (
                <div className="flex flex-wrap gap-2 flex-1">
                  {earned.slice(0, 10).map((b) => (
                    <AchievementBadge key={b.badge_id} badge={b} size="sm" />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground flex-1">
                  No badges yet — finish a session to start collecting.
                </p>
              )}

              {nextUp && (
                <div className="lg:w-72" data-testid="achievements-next-up">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">Next: {nextUp.title}</span>
                    <span className="text-xs text-muted-foreground">{nextUp.progress_label}</span>
                  </div>
                  <Progress value={nextUp.progress_pct} className="h-2" />
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="achievements-grid">
      {data.badges.map((b) => (
        <Card
          key={b.badge_id}
          className={`bg-card border-border ${b.earned ? "" : "opacity-70"}`}
          data-testid={`achievement-card-${b.badge_id}`}
        >
          <CardContent className="p-5 flex gap-4">
            <AchievementBadge badge={b} />
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline justify-between gap-2">
                <p className="font-semibold truncate">{b.title}</p>
                {b.earned && (
                  <span className="text-xs text-primary whitespace-nowrap">
                    {formatDate(b.earned_at) || "Earned"}
                  </span>
                )}
              </div>
              <p className="text-sm text-muted-foreground mb-2">{b.description}</p>
              {b.earned ? (
                <p className="text-xs text-primary uppercase tracking-wide">Earned</p>
              ) : (
                <>
                  <Progress value={b.progress_pct} className="h-1.5 mb-1" />
                  <p className="text-xs text-muted-foreground">{b.progress_label}</p>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default AchievementsCard;
